import React, { useState, useEffect, useRef } from 'react'
import DOMPurify from 'dompurify'
import { getSiteConfig, getCompanyContents } from '../services/api'

const Company = () => {
  const [siteConfig, setSiteConfig] = useState(null)
  const [companyInfo, setCompanyInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('intro')

  const introRef = useRef(null)
  const innovationRef = useRef(null)
  const honorsRef = useRef(null)
  const cultureRef = useRef(null)
  const historyRef = useRef(null)

  useEffect(() => {
    const loadData = async () => {
      try {
        const [config, companyData] = await Promise.all([
          getSiteConfig(),
          getCompanyContents()
        ])
        setSiteConfig(config)
        setCompanyInfo(companyData)
      } catch (error) {
        console.error('加载数据失败:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const scrollToSection = (ref, tabName) => {
    setActiveTab(tabName)
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // 安全渲染内容：支持 Quill 富文本HTML，也兼容旧版纯文本（\n\n 分隔段落）
  const renderContent = (content) => {
    if (!content) return null
    if (content.startsWith('<')) {
      // HTML 富文本，用 DOMPurify 清洗后渲染
      return (
        <div
          className="rich-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
              'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
              'ul', 'ol', 'li', 'blockquote', 'a', 'span',
              'img', 'hr', 'table', 'thead', 'tbody', 'tr', 'td', 'th'],
            ALLOWED_ATTR: ['href', 'target', 'style', 'class', 'src', 'alt', 'width', 'height']
          }) }}
        />
      )
    }
    // 旧版纯文本，按 \n\n 分段
    return content.split('\n\n').map((paragraph, index) => (
      <p key={index}>{paragraph}</p>
    ))
  }

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  const siteName = siteConfig?.site?.name || '无锡市摩恒科技有限公司'

  const companyData = companyInfo || {}

  // 动态子导航标签：仅显示有数据的条目（后端已按 is_active 过滤）
  const tabItems = []
  if (companyData.introduction) tabItems.push({ key: 'intro', label: '公司简介', ref: introRef })
  if (companyData.innovation) tabItems.push({ key: 'innovation', label: '科技创新', ref: innovationRef })
  if (companyData.honors && companyData.honors.length > 0) tabItems.push({ key: 'honors', label: '认证与荣誉', ref: honorsRef })
  if (companyData.values) tabItems.push({ key: 'culture', label: '企业文化', ref: cultureRef })
  if (companyData.history && companyData.history.length > 0) tabItems.push({ key: 'history', label: '发展历程', ref: historyRef })

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      {/* Page Banner - 匹配参考页面风格 */}
      <div className="bg-primary py-16">
        <div className="container-max text-center">
          <h1 className="text-3xl md:text-4xl font-light text-white text-opacity-80 mb-2 tracking-widest">
            无锡市摩恒科技有限公司
          </h1>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-8">
            Wuxi Moheng Technology Co., Ltd.
          </h3>
          {/* 子导航标签 - 动态显示，仅包含有数据的条目 */}
          <div className="flex flex-wrap justify-center gap-2">
            {tabItems.length > 0 ? (
              tabItems.map(item => (
                <button
                  key={item.key}
                  onClick={() => scrollToSection(item.ref, item.key)}
                  className={`px-5 py-2 text-sm rounded-full transition-all duration-300 border ${
                    activeTab === item.key
                      ? 'bg-white text-primary border-white'
                      : 'text-white border-white border-opacity-40 hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  {item.label}
                </button>
              ))
            ) : (
              <span className="text-white text-opacity-60 text-sm">暂无内容</span>
            )}
          </div>
        </div>
      </div>

      <div className="container-max section-padding">
        {/* ===== 1. 公司简介 ===== */}
        {companyData.introduction && (
        <section ref={introRef} className="mb-16 scroll-mt-24">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 lg:p-12">
                <h3 className="text-3xl font-bold text-gray-tech mb-6">
                  {companyData.introduction?.title || '公司简介'}
                </h3>
                <div className="text-gray-600 leading-relaxed space-y-4">
                  {companyData.introduction?.content ? (
                    renderContent(companyData.introduction.content)
                  ) : (
                    <p>{siteName}，专注于工业自动化、智能制造和数字化转型领域，致力于为客户提供全方位的技术解决方案和优质服务。公司自成立以来，始终坚持创新驱动发展战略，持续加大研发投入，建立完善的科技创新体系。</p>
                  )}
                </div>
              </div>
              <div className="bg-gradient-to-br from-primary to-blue-700 flex items-center justify-center p-8">
                <div className="w-full h-full flex flex-col items-center justify-center">
                  {companyData.introduction?.image_url || companyData.introduction?.logo_url ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={companyData.introduction?.image_url || companyData.introduction?.logo_url}
                        alt="摩恒科技形象展示"
                        className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                        style={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%' }}
                      />
                    </div>
                  ) : (
                    <div className="text-white text-center">
                      <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl">M</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-2">摩恒科技</h3>
                      <p className="text-primary-light">Moheng Technology</p>
                      <p className="mt-4 text-sm opacity-90">创新科技 · 引领未来</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </section>
        )}

        {/* ===== 2. 科技创新 ===== */}
        {companyData.innovation && (
          <section ref={innovationRef} className="mb-16 scroll-mt-24">
            <div className="text-center mb-10">
              <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Technological Innovation</p>
              <h2 className="text-3xl font-bold text-gray-tech">{companyData.innovation.title || '科技创新'}</h2>
            </div>
            {companyData.innovation.description && (
              <p className="text-gray-600 text-center max-w-3xl mx-auto mb-10 leading-relaxed">
                {companyData.innovation.description}
              </p>
            )}
            {(companyData.innovation.features && companyData.innovation.features.length > 0) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {companyData.innovation.features.map((feature, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-primary hover:shadow-lg transition-all duration-300">
                    <h3 className="text-lg font-semibold text-gray-tech mb-3">{feature.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">暂无科技创新数据</p>
              </div>
            )}
          </section>
        )}

        {/* ===== 3. 荣誉资质 ===== */}
        {companyData.honors && companyData.honors.length > 0 && (
        <section ref={honorsRef} className="mb-16 scroll-mt-24">
          <div className="text-center mb-10">
            <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Certification and Honors</p>
            <h2 className="text-3xl font-bold text-gray-tech">荣誉资质</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companyData.honors && companyData.honors.length > 0 ? (
              companyData.honors.map((honor, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  {/* 图片或占位图标 */}
                  <div className="h-44 bg-gray-100 flex items-center justify-center overflow-hidden">
                    {honor.image ? (
                      <img
                        src={honor.image}
                        alt={honor.title}
                        className="w-full h-full object-contain p-4"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                        <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-gray-tech mb-1">{honor.title}</h3>
                    {honor.year && <p className="text-primary text-sm font-medium mb-2">{honor.year}</p>}
                    {honor.description && <p className="text-gray-600 text-sm">{honor.description}</p>}
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center py-8">
                <p className="text-gray-500">暂无荣誉资质数据</p>
              </div>
            )}
          </div>
        </section>
        )}

        {/* ===== 4. 企业文化 ===== */}
        {companyData.values && (
        <section ref={cultureRef} className="mb-16 scroll-mt-24">
          <div className="text-center mb-10">
            <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Corporate Culture</p>
            <h2 className="text-3xl font-bold text-gray-tech">企业文化</h2>
          </div>

          <div className="space-y-8">
            {/* 使命 & 愿景 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-primary to-blue-600 rounded-lg shadow-md p-8 text-white">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 mr-3 text-white text-opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <h3 className="text-xl font-semibold">企业使命</h3>
                </div>
                <div className="text-white text-opacity-90 text-lg leading-relaxed">
                  {companyData.values?.mission ? renderContent(companyData.values.mission) : '为客户提供最优质的智能制造解决方案，推动制造业数字化转型'}
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg shadow-md p-8 text-white">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 mr-3 text-white text-opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <h3 className="text-xl font-semibold">企业愿景</h3>
                </div>
                <div className="text-white text-opacity-90 text-lg leading-relaxed">
                  {companyData.values?.vision ? renderContent(companyData.values.vision) : '成为国内领先的工业自动化解决方案提供商'}
                </div>
              </div>
            </div>

            {/* 核心价值观 */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h3 className="text-xl font-semibold text-gray-tech mb-6 text-center">核心价值观</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {(companyData.values?.core_values && companyData.values.core_values.length > 0) ? (
                  companyData.values.core_values.map((item, index) => (
                    <div key={index} className="text-center p-4 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                      <div className="text-3xl mb-3">{item.icon}</div>
                      <h4 className="font-semibold text-gray-tech mb-2">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.description}</p>
                    </div>
                  ))
                ) : (
                  <>
                    {['创新驱动', '客户至上', '诚信经营', '合作共赢'].map((title, index) => (
                      <div key={index} className="text-center p-4 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                        <div className="text-3xl mb-3">{['🚀', '🤝', '🎯', '💎'][index]}</div>
                        <h4 className="font-semibold text-gray-tech mb-2">{title}</h4>
                        <p className="text-sm text-gray-600">追求卓越品质与服务</p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* 经营理念 & 研发理念 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-primary">
                <div className="flex items-center mb-3">
                  <svg className="w-5 h-5 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-tech">经营理念</h3>
                </div>
                <div className="text-gray-600 leading-relaxed">
                  {companyData.values?.philosophy ? renderContent(companyData.values.philosophy) : '以质量求生存、以科技增效益，以品牌促发展，以客户为中心'}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-blue-500">
                <div className="flex items-center mb-3">
                  <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-tech">研发理念</h3>
                </div>
                <div className="text-gray-600 leading-relaxed">
                  {companyData.values?.rd_philosophy ? renderContent(companyData.values.rd_philosophy) : '科技、创新、创造'}
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* ===== 5. 发展历程 ===== */}
        {companyData.history && companyData.history.length > 0 && (
        <section ref={historyRef} className="mb-16 scroll-mt-24">
          <div className="text-center mb-10">
            <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Development History</p>
            <h2 className="text-3xl font-bold text-gray-tech">发展历程</h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-primary h-full hidden md:block"></div>
            <div className="space-y-12">
              {companyData.history && companyData.history.length > 0 ? (
                companyData.history.map((item, index) => (
                  <div key={index} className={`relative flex flex-col md:flex-row items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                      <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="text-primary font-bold text-lg mb-2">{item.year}</div>
                        <h3 className="text-xl font-semibold text-gray-tech mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary rounded-full border-4 border-white shadow-lg z-10"></div>
                    <div className="w-full md:w-1/2"></div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">暂无发展历程数据</p>
                </div>
              )}
            </div>
          </div>
        </section>
        )}

        {/* ===== 6. 核心团队（保留原有板块） ===== */}
        {companyData.team && companyData.team.length > 0 && (
          <section className="mb-16">
            <div className="text-center mb-10">
              <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Core Team</p>
              <h2 className="text-3xl font-bold text-gray-tech">核心团队</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {companyData.team.map((member, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <img
                    src={member.image || '/api/placeholder/300/300'}
                    alt={member.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-tech mb-1">{member.name}</h3>
                    <p className="text-primary font-medium mb-3">{member.position}</p>
                    <p className="text-gray-600 text-sm">{member.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="bg-primary rounded-lg shadow-md">
          <div className="text-center text-white p-12">
            <h2 className="text-3xl font-bold mb-4">加入我们，共创未来</h2>
            <p className="text-xl text-primary-light mb-8 max-w-2xl mx-auto">
              探索我们的产品与服务，了解更多创新技术解决方案
            </p>
            <div className="flex justify-center">
              <a
                href="/products"
                className="bg-white text-primary hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-200 inline-block"
              >
                查看产品
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Company
